export type MultiFilterOperator = "any" | "all" | "none";

export const MULTI_FILTER_OPERATORS: MultiFilterOperator[] = [
  "any",
  "all",
  "none",
];

export interface ParsedMultiParameter {
  operator: MultiFilterOperator;
  values: string[];
}

const OPERATOR_SEPARATOR = ":";
const VALUE_SEPARATOR = ",";

function isOperator(
  value: string | null | undefined,
): value is MultiFilterOperator {
  return !!value && (MULTI_FILTER_OPERATORS as string[]).includes(value);
}

// `any` is the default operator and is omitted from the serialized form so
// single-value URLs stay visually compatible with the legacy `owner=42` shape.
// An operator prefix on `raw` (e.g. `none:1,2`) always wins; a standalone
// `fallbackOperator` (used by Default View snapshots that store the operator
// in a separate URL param) only applies when no prefix is present.
export function parseMultiParameter(
  raw: string | null | undefined,
  fallbackOperator?: string | null,
): ParsedMultiParameter {
  if (!raw) {
    return { operator: "any", values: [] };
  }

  const separatorIndex = raw.indexOf(OPERATOR_SEPARATOR);
  let operator: MultiFilterOperator = "any";
  let valuesString = raw;
  let operatorWasExplicit = false;

  if (separatorIndex !== -1) {
    const maybeOperator = raw.slice(0, separatorIndex);
    if (isOperator(maybeOperator)) {
      operator = maybeOperator;
      valuesString = raw.slice(separatorIndex + 1);
      operatorWasExplicit = true;
    }
  }

  if (!operatorWasExplicit && isOperator(fallbackOperator)) {
    operator = fallbackOperator;
  }

  const values = valuesString
    .split(VALUE_SEPARATOR)
    .map((value) => value.trim())
    .filter((value) => value.length > 0);

  return { operator, values };
}

export function serializeMultiParameter(
  operator: MultiFilterOperator,
  values: string[],
): string {
  const cleaned = values
    .map((value) => String(value).trim())
    .filter((value) => value.length > 0);

  if (cleaned.length === 0) {
    return "";
  }

  const joined = cleaned.join(VALUE_SEPARATOR);

  if (operator === "any") {
    return joined;
  }

  return `${operator}${OPERATOR_SEPARATOR}${joined}`;
}
